import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Loader2, Download, Copy, RefreshCw, Table2, ExternalLink, AlertTriangle,
  Database, PhoneOff, MailX, BadgeCheck, ArrowUp, ArrowDown, ArrowUpDown,
  X, ChevronDown, AtSign, Check,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import DirectoryCountryPanel from '../components/DirectoryCountryPanel';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

// Column order is also the CSV column order.
const COLUMNS = [
  { key: 'name', label: 'name', width: 'min-w-[200px]' },
  { key: 'website', label: 'website', width: 'min-w-[190px]' },
  { key: 'emails', label: 'emails', width: 'min-w-[210px]' },
  { key: 'phones', label: 'phones', width: 'min-w-[140px]' },
  { key: 'country', label: 'country', width: 'min-w-[130px]' },
  { key: 'city', label: 'city', width: 'min-w-[130px]' },
  { key: 'commodities', label: 'commodities', width: 'min-w-[170px]' },
  { key: 'principal_name', label: 'principal_name', width: 'min-w-[150px]' },
  { key: 'principal_email', label: 'principal_email', width: 'min-w-[190px]' },
  { key: 'claim_status', label: 'claim_status', width: 'min-w-[130px]' },
  { key: 'source', label: 'source', width: 'min-w-[110px]' },
  { key: 'created_at', label: 'created_at', width: 'min-w-[100px]' },
];

// These filter by exact value via a searchable dropdown; the rest are substring inputs.
const DROPDOWN_COLUMNS = new Set(['country', 'city', 'claim_status', 'source']);
const BLANK_TOKEN = '(none)';

const EMPTY_COL_FILTERS = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: '' }), {});

const csvEscape = (value) => {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (rows) =>
  [COLUMNS.map((c) => c.label).join(',')]
    .concat(rows.map((r) => COLUMNS.map((c) => csvEscape(r[c.key])).join(',')))
    .join('\n');

const isBlank = (v) => !String(v ?? '').trim();

// emails/phones arrive comma-joined from the API
const splitList = (v) => String(v ?? '').split(',').map((s) => s.trim()).filter(Boolean);

/**
 * Small combobox: a trigger button plus a searchable option list.
 * The panel is position:fixed so the table's overflow container can't clip it.
 */
const SearchableSelect = ({ value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 190) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    searchRef.current?.focus();
    const onDocDown = (e) => {
      if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    // reposition while the table scrolls underneath
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.value.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const pick = (v) => { onChange(v); setOpen(false); setQuery(''); };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-1 px-2 py-1 border rounded text-[11px] font-mono bg-white hover:border-gray-400 ${
          value ? 'border-blue-400 text-gray-900 font-semibold' : 'border-gray-200 text-gray-400 font-normal'
        }`}
        title={value || 'all'}
      >
        <span className="truncate">{value || 'all'}</span>
        {value ? (
          <X
            className="w-3 h-3 flex-shrink-0 hover:text-red-500"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
          />
        ) : (
          <ChevronDown className="w-3 h-3 flex-shrink-0 opacity-60" />
        )}
      </button>

      {open && pos && (
        <div
          ref={panelRef}
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          <div className="p-1.5 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                data-testid={`${testId}-search`}
                className="w-full pl-6 pr-2 py-1 border border-gray-200 rounded text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => pick('')}
              className="w-full flex items-center justify-between px-2.5 py-1 text-[11px] font-mono hover:bg-gray-50 text-gray-500"
            >
              <span>all</span>
              {!value && <Check className="w-3 h-3 text-blue-600" />}
            </button>
            {shown.length === 0 ? (
              <p className="px-2.5 py-2 text-[11px] text-gray-400">No matches</p>
            ) : shown.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1 text-[11px] font-mono hover:bg-gray-50 ${
                  value === o.value ? 'bg-blue-50 text-blue-800' : 'text-gray-700'
                }`}
              >
                <span className="truncate" title={o.value}>{o.value}</span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-gray-400">{o.count}</span>
                  {value === o.value && <Check className="w-3 h-3 text-blue-600" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const AdminDirectoryDump = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS);
  const [quickFilter, setQuickFilter] = useState(null); // missing_phone | missing_email | claimed
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/directory/dump');
      setRows(res.data.rows || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load directory');
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Dropdown options come from the full dataset so choices never disappear mid-filter.
  const dropdownOptions = useMemo(() => {
    const out = {};
    DROPDOWN_COLUMNS.forEach((key) => {
      const counts = new Map();
      rows.forEach((r) => {
        const v = isBlank(r[key]) ? BLANK_TOKEN : String(r[key]).trim();
        counts.set(v, (counts.get(v) || 0) + 1);
      });
      out[key] = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    });
    return out;
  }, [rows]);

  const matchesColumn = (row, key, val) => {
    const cell = row[key];
    if (DROPDOWN_COLUMNS.has(key)) {
      if (val === BLANK_TOKEN) return isBlank(cell);
      return String(cell ?? '').trim().toLowerCase() === val.trim().toLowerCase();
    }
    return String(cell ?? '').toLowerCase().includes(val.trim().toLowerCase());
  };

  // Search + per-column filters. Stats come from THIS set, so the stat cards stay
  // meaningful targets you can click (the quick filter is applied after).
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const active = Object.entries(colFilters).filter(([, v]) => v.trim());
    return rows.filter((r) => {
      if (q && !COLUMNS.some((c) => String(r[c.key] ?? '').toLowerCase().includes(q))) return false;
      return active.every(([key, val]) => matchesColumn(r, key, val));
    });
  }, [rows, search, colFilters]);

  const stats = useMemo(() => {
    let emailAddresses = 0;
    let withEmail = 0;
    baseFiltered.forEach((r) => {
      const n = splitList(r.emails).length;
      emailAddresses += n;
      if (n) withEmail += 1;
    });
    return {
      total: baseFiltered.length,
      emailAddresses,
      withEmail,
      missingPhone: baseFiltered.filter((r) => isBlank(r.phones)).length,
      missingEmail: baseFiltered.length - withEmail,
      claimed: baseFiltered.filter((r) => r.claim_status === 'approved').length,
    };
  }, [baseFiltered]);

  const countryStats = useMemo(() => {
    const counts = new Map();
    baseFiltered.forEach((r) => {
      const c = isBlank(r.country) ? BLANK_TOKEN : String(r.country).trim();
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count || a.country.localeCompare(b.country));
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    let out = baseFiltered;
    if (quickFilter === 'missing_phone') out = out.filter((r) => isBlank(r.phones));
    else if (quickFilter === 'missing_email') out = out.filter((r) => isBlank(r.emails));
    else if (quickFilter === 'claimed') out = out.filter((r) => r.claim_status === 'approved');

    if (sort.key) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = String(a[sort.key] ?? '').trim();
        const bv = String(b[sort.key] ?? '').trim();
        if (!av && !bv) return 0;
        if (!av) return 1;   // blanks always last
        if (!bv) return -1;
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' }) * dir;
      });
    }
    return out;
  }, [baseFiltered, quickFilter, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }));
  };

  const setColFilter = (key, val) => setColFilters((f) => ({ ...f, [key]: val }));

  const anyFilterActive = !!(search || quickFilter || Object.values(colFilters).some((v) => v.trim()));

  const clearAll = () => {
    setSearch('');
    setColFilters(EMPTY_COL_FILTERS);
    setQuickFilter(null);
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([buildCsv(filtered)], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trados_directory_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filtered.length.toLocaleString()} rows`);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCsv(filtered));
      toast.success(`Copied ${filtered.length.toLocaleString()} rows as CSV`);
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color, active, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-white rounded-xl border p-4 transition hover:shadow-md ${
        active ? '' : 'border-gray-200'
      } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      style={active ? { borderColor: color, boxShadow: `0 0 0 2px ${color}55` } : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {active && <X className="w-3.5 h-3.5 text-gray-400" />}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </button>
  );

  const pct = (n) => (stats.total ? Math.round((n / stats.total) * 100) : 0);

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-full mx-auto space-y-4">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Table2 className="w-5 h-5" style={{ color: BRAND_BLUE }} />
                Company Directory — Data View
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Every listing in the database, one per line. CSV-style for scanning and export.
              </p>
            </div>
            <Link
              to="/admin/directory"
              className="text-sm font-medium hover:underline flex items-center gap-1"
              style={{ color: BRAND_BLUE }}
            >
              Manage directory <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>

          {!loading && !error && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatCard
                  testId="stat-total-records"
                  icon={Database} label="Total Records" value={stats.total} color={BRAND_BLUE}
                  sub={anyFilterActive ? `of ${rows.length.toLocaleString()} in database` : 'all listings'}
                />
                <StatCard
                  testId="stat-total-emails"
                  icon={AtSign} label="Total Emails" value={stats.emailAddresses} color="#7C3AED"
                  sub={`across ${stats.withEmail.toLocaleString()} companies`}
                />
                <StatCard
                  testId="stat-missing-phone"
                  icon={PhoneOff} label="Missing Phone" value={stats.missingPhone} color="#D97706"
                  sub={`${pct(stats.missingPhone)}% of shown`}
                  active={quickFilter === 'missing_phone'}
                  onClick={() => setQuickFilter(quickFilter === 'missing_phone' ? null : 'missing_phone')}
                />
                <StatCard
                  testId="stat-missing-emails"
                  icon={MailX} label="Missing Emails" value={stats.missingEmail} color="#DC2626"
                  sub={`${pct(stats.missingEmail)}% of shown`}
                  active={quickFilter === 'missing_email'}
                  onClick={() => setQuickFilter(quickFilter === 'missing_email' ? null : 'missing_email')}
                />
                <StatCard
                  testId="stat-claimed"
                  icon={BadgeCheck} label="Claimed" value={stats.claimed} color="#059669"
                  sub={`${pct(stats.claimed)}% of shown`}
                  active={quickFilter === 'claimed'}
                  onClick={() => setQuickFilter(quickFilter === 'claimed' ? null : 'claimed')}
                />
              </div>

              {/* Per country — shared with the public /directory page */}
              <DirectoryCountryPanel
                countries={countryStats}
                selected={colFilters.country}
                onSelect={(c) => setColFilter('country', c)}
              />
            </>
          )}

          {/* Toolbar */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  data-testid="dump-search"
                  placeholder="Filter across every column..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <button
                onClick={fetchRows}
                disabled={loading}
                data-testid="dump-refresh"
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              {anyFilterActive && (
                <button
                  onClick={clearAll}
                  data-testid="dump-clear"
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                  Clear filters
                </button>
              )}

              <button
                onClick={handleCopy}
                disabled={loading || filtered.length === 0}
                data-testid="dump-copy"
                className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Copy className="w-4 h-4" />
                Copy CSV
              </button>

              <button
                onClick={handleDownload}
                disabled={loading || filtered.length === 0}
                data-testid="dump-download"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>

              <span className="text-sm text-gray-400 whitespace-nowrap">
                {anyFilterActive
                  ? `${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()}`
                  : `${rows.length.toLocaleString()} companies`}
              </span>
            </div>
          </div>

          {/* Data */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                <p className="text-sm">Loading every listing...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <AlertTriangle className="w-8 h-8 mb-2 text-red-400" />
                <p className="text-sm font-medium text-red-600">{error}</p>
                <button
                  onClick={fetchRows}
                  className="mt-3 px-3 py-1.5 text-xs font-medium text-white rounded-lg"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  Try again
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Table2 className="w-8 h-8 mb-2" />
                <p className="text-sm">
                  {rows.length === 0 ? 'No companies in the directory yet' : 'No rows match those filters'}
                </p>
                {anyFilterActive && (
                  <button onClick={clearAll} className="mt-2 text-xs text-blue-600 hover:underline">
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-auto max-h-[calc(100vh-320px)]">
                <table className="w-full text-xs font-mono border-collapse">
                  <thead className="sticky top-0 z-10">
                    {/* sortable labels */}
                    <tr className="bg-gray-100">
                      <th className="text-right px-2 py-2 font-semibold text-gray-500 border-b border-gray-200 sticky left-0 bg-gray-100">
                        #
                      </th>
                      {COLUMNS.map((c) => {
                        const active = sort.key === c.key;
                        const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                        return (
                          <th
                            key={c.key}
                            className={`text-left px-3 py-2 border-b border-gray-200 whitespace-nowrap ${c.width}`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort(c.key)}
                              data-testid={`sort-${c.key}`}
                              title={`Sort by ${c.label}`}
                              className={`flex items-center gap-1 font-semibold hover:text-gray-900 ${
                                active ? 'text-gray-900' : 'text-gray-600'
                              }`}
                            >
                              {c.label}
                              <Icon className={`w-3 h-3 ${active ? '' : 'opacity-30'}`} />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                    {/* per-column filters */}
                    <tr className="bg-gray-50">
                      <th className="px-2 py-1.5 border-b border-gray-300 sticky left-0 bg-gray-50">
                        {Object.values(colFilters).some((v) => v.trim()) && (
                          <button
                            type="button"
                            onClick={() => setColFilters(EMPTY_COL_FILTERS)}
                            title="Clear column filters"
                            className="text-gray-400 hover:text-gray-700"
                          >
                            <X className="w-3 h-3 mx-auto" />
                          </button>
                        )}
                      </th>
                      {COLUMNS.map((c) => (
                        <th key={c.key} className="px-2 py-1.5 border-b border-gray-300">
                          {DROPDOWN_COLUMNS.has(c.key) ? (
                            <SearchableSelect
                              testId={`colfilter-${c.key}`}
                              value={colFilters[c.key]}
                              options={dropdownOptions[c.key] || []}
                              onChange={(v) => setColFilter(c.key, v)}
                            />
                          ) : (
                            <input
                              type="text"
                              value={colFilters[c.key]}
                              data-testid={`colfilter-${c.key}`}
                              onChange={(e) => setColFilter(c.key, e.target.value)}
                              placeholder="filter..."
                              className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] font-mono font-normal bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row, idx) => (
                      <tr
                        key={row.id}
                        className={`${idx % 2 ? 'bg-gray-50/60' : 'bg-white'} hover:bg-blue-50/60`}
                      >
                        <td className="px-2 py-1.5 text-right text-gray-400 border-b border-gray-100 sticky left-0 bg-inherit">
                          {idx + 1}
                        </td>
                        {COLUMNS.map((c) => (
                          <td
                            key={c.key}
                            title={row[c.key] || ''}
                            className="px-3 py-1.5 text-gray-700 border-b border-gray-100 whitespace-nowrap max-w-[280px] overflow-hidden text-ellipsis"
                          >
                            {c.key === 'name' && row.slug ? (
                              <a
                                href={`/directory/${row.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline"
                                style={{ color: BRAND_BLUE }}
                              >
                                {row.name}
                              </a>
                            ) : (
                              row[c.key] || <span className="text-gray-300">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDirectoryDump;
